import React, { useState } from 'react';
import { generateMermaidCode, copyToClipboard } from '../utils/mermaidGenerator';

const Sidebar = ({ nodes, connections, isOpen, onToggle }) => {
  const [activeTab, setActiveTab] = useState('code');
  const [copied, setCopied] = useState(false);

  const mermaidCode = generateMermaidCode(nodes, connections);

  const handleCopy = async () => {
    const success = await copyToClipboard(mermaidCode);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="absolute right-0 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 border border-r-0 border-gray-200 dark:border-gray-700 rounded-l-lg p-2 shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title="Show sidebar"
      >
        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    );
  }

  return (
    <div className="w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setActiveTab('code')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'code'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            Mermaid
          </button>
          <button
            onClick={() => setActiveTab('help')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'help'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            Help
          </button>
        </div>
        <button
          onClick={onToggle}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Hide sidebar"
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'code' && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Generated Code
              </h3>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              >
                {copied ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
            <pre className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-xs text-gray-700 dark:text-gray-300 overflow-x-auto font-mono whitespace-pre-wrap">
              {mermaidCode}
            </pre>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Use this code with{' '}
              <a
                href="https://mermaid.live"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                Mermaid Live Editor
              </a>
              {' '}or in Markdown files.
            </p>
          </div>
        )}

        {activeTab === 'help' && (
          <div className="p-4 space-y-6">
            {/* Quick Start */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Quick Start
              </h3>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">1.</span>
                  <span>Click a node type in the toolbar to add it</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">2.</span>
                  <span>Drag nodes to position them</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">3.</span>
                  <span>Select a node, click "Connect", then click target</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">4.</span>
                  <span>Double-click to edit nodes or connections</span>
                </li>
              </ul>
            </div>

            {/* Keyboard Shortcuts */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Keyboard Shortcuts
              </h3>
              <div className="space-y-1.5">
                {[
                  { keys: 'Delete / Backspace', action: 'Delete selected' },
                  { keys: 'Ctrl + S', action: 'Save project' },
                  { keys: 'Ctrl + N', action: 'New project' },
                  { keys: 'Ctrl + O', action: 'Open project' },
                  { keys: 'Ctrl + E', action: 'Export' },
                  { keys: 'Ctrl + Z', action: 'Undo' },
                  { keys: 'Ctrl + Shift + Z', action: 'Redo' },
                  { keys: 'Ctrl + C', action: 'Copy node' },
                  { keys: 'Escape', action: 'Cancel / Deselect' },
                  { keys: 'Space + Drag', action: 'Pan canvas' },
                  { keys: 'Ctrl + Scroll', action: 'Zoom in/out' }
                ].map((shortcut) => (
                  <div key={shortcut.keys} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{shortcut.action}</span>
                    <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-mono">
                      {shortcut.keys}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>

            {/* Node Types */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Node Types
              </h3>
              <div className="space-y-2">
                {[
                  { shape: '▭', name: 'Rectangle', desc: 'Process or action step' },
                  { shape: '⬭', name: 'Rounded', desc: 'Start or end terminal' },
                  { shape: '◇', name: 'Diamond', desc: 'Decision or branch point' },
                  { shape: '▱', name: 'Parallelogram', desc: 'Input or output' }
                ].map((type) => (
                  <div key={type.name} className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded text-lg">
                      {type.shape}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{type.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{type.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Tips
              </h3>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-green-500">•</span>
                  <span>Your work auto-saves to browser storage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">•</span>
                  <span>Use templates for common flowchart patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">•</span>
                  <span>Export as .flowise to share and edit later</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500">•</span>
                  <span>Connection labels appear between nodes</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{nodes.length} nodes</span>
          <span>{connections.length} connections</span>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
